import { Fragment } from "react";

import { Skeleton } from "@/components";
import { useUser } from "@/providers/context";

import { CommentInput } from "@/app/modules/Comments/components/CommentInput.tsx";
import { CommentWithResponses } from "@/app/modules/Comments/components/CommentWithResponses.tsx";
import { usePutComment } from "@/app/modules/Comments/hooks/usePutComment.tsx";
import { cn } from "@/utils";
import { useCommentsContext } from "./providers/context/useCommentsContext";

const Line = ({ className }: { className: string }) => (
  <div className={cn("rounded-[16px] bg-surface-thumbnail", className)} />
);

const SkeletonSection = () => (
  <Skeleton className="flex w-full flex-col gap-2 bg-transparent pb-4 pt-[1px]">
    <div className="flex gap-3">
      <div className="h-[36px] w-[36px] shrink-0 rounded-full bg-surface-thumbnail" />
      <div className="flex h-full w-full flex-col justify-center gap-2">
        <Line className="h-[14px] w-[90px]" />
        <Line className="h-[12px] w-[52px]" />
      </div>
    </div>
    <div className="flex w-full flex-col gap-2">
      <Line className="h-[16px] w-full" />
      <Line className="h-[16px] w-1/2" />
    </div>
  </Skeleton>
);

export const CommentsInner = () => {
  const { user_name } = useUser();
  const {
    itemId,
    itemType,
    dataSource,
    sourceId,
    showHeader,
    sortedComments,
    setPendingComment,
    labelWithCount,
    defaultPendingComment,
    userThumbnail,
  } = useCommentsContext();

  const putComment = usePutComment();

  // If the user is not logged in, show nothing
  if (!user_name) {
    return null;
  }

  return (
    <>
      {showHeader && (
        <div className="p1-bold mb-10 mt-12 text-item-primary">
          {labelWithCount}
        </div>
      )}
      <div className="mb-10">
        <CommentInput
          topLevel
          putComment={(comment) => {
            setPendingComment({
              ...defaultPendingComment,
              Comment: comment,
              CommentDate: new Date().toISOString(),
            });

            putComment.mutate(
              {
                comment,
                sourceId,
                dataSource,
                itemId,
                itemType,
                thumbnailSource: userThumbnail,
              },
              {
                onSuccess: () => setPendingComment(null),
                onError: () => setPendingComment(null),
              },
            );
          }}
        />
      </div>
      <div className="flex flex-col gap-10">
        {sortedComments
          ? sortedComments.map((c) => (
              <Fragment key={c.SourceCommentId}>
                <CommentWithResponses comment={c} topLevel={true} />
                <div className="border-b border-border" />
              </Fragment>
            ))
          : [1, 2, 3].map((i) => <SkeletonSection key={i} />)}
      </div>
    </>
  );
};
